import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useFirestoreDoc } from './useFirestoreDoc';
import { Client } from '../types';
import { callGeminiAPI } from '../lib/geminiFunction';

// Define the structure for AI model data stored in Firestore
interface AIModelData {
  learningData?: {
    interactions?: Array<{
      prompt: string;
      response: string;
      timestamp: string;
      clientId: string | null;
      conversationId: string | null;
    }>;
    feedback?: Array<{
      messageId: string;
      type: 'positive' | 'negative';
      details: string;
      timestamp: string;
    }>;
  };
  lastInteractedAt?: string;
}

/**
 * Custom hook for interacting with Gemini AI
 * @returns Object with AI message generation and training functions
 */
export const useGeminiAI = () => {
  const { currentUser } = useAuth(); // Corrected: use currentUser
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Get user's AI model data from Firestore
  const { data: aiModelData, saveData: saveModelData } = useFirestoreDoc<AIModelData>( // Specify the type argument
    'aiModels', 
    currentUser?.uid // Corrected: use currentUser
  );

  // Generate PR-focused response with AI, potentially with client context
  const generateResponse = useCallback(async (prompt: string, options: {
    messageContext?: string[];
    client?: Client | null;
    conversationId?: string;
    additionalContext?: string;
  } = {}) => {
    const { messageContext = [], client = null, conversationId, additionalContext } = options;
    
    setIsGenerating(true);
    setError(null);
    
    try {
      // Format client-specific context if available
      let clientContextString = '';
      if (client) {
        clientContextString = `
          CLIENT CONTEXT:
          Client Name: ${client.name}
          Industry: ${client.industry}
          Description: ${client.description}
          
          KEY MESSAGING GUIDELINES:
          Brand Tone: ${client.context.brandTone}
          Key Messages: ${client.context.keyMessages.join(', ')}
          Approved Language/Phrases: ${client.context.approvedLanguage.join(', ')}
          DO NOT USE: ${client.context.forbiddenLanguage.join(', ')}
          
          TARGET AUDIENCE:
          ${client.context.targetAudience.join(', ')}
          
          ACTIVE CAMPAIGNS:
          ${client.context.currentCampaigns
            .filter(c => c.status === 'active')
            .map(c => `- ${c.name}: ${c.description}`)
            .join('\n')}
          
          COMPETITORS:
          ${client.context.competitors.join(', ')}
          
          CRISIS PROTOCOL:
          ${client.context.crisisProtocol || 'No specific crisis protocol defined.'}
          
          NOTES:
          ${client.context.notes || 'No additional notes.'}
          
          ${additionalContext ? additionalContext : ''}
        `;
        console.log("Formatted Client Context String for AI:", clientContextString); // Added this line
      }
      
      // Format the prompt with PR-specific instruction and client context if available
      const formattedPrompt = `
        You are an AI assistant for PR professionals. 
        Focus on providing clear, professional communication advice.
        
        ${clientContextString}
        
        ${messageContext.length > 0 ? 'CONTEXT FROM PREVIOUS MESSAGES:\n' + messageContext.join('\n') : ''}
        
        User request: ${prompt}
      `;
      
      // Call the Firebase Cloud Function instead of direct API call
      const text = await callGeminiAPI(formattedPrompt, 'gemini-2.5-flash');
      
      // Save user interaction for improving the AI model
      if (currentUser?.uid) { // Corrected: use currentUser
        const newInteraction = {
          prompt,
          response: text,
          timestamp: new Date().toISOString(),
          clientId: client?.id || null,
          conversationId: conversationId || null
        };
        
        await saveModelData({
          learningData: {
            interactions: [
              ...(aiModelData?.learningData?.interactions || []),
              newInteraction
            ].slice(-100) // Keep last 100 interactions
          },
          lastInteractedAt: new Date().toISOString()
        }, true);
      }
      
      return text;
    } catch (err) {
      console.error('Error generating AI response:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return 'I apologize, but I encountered an error while processing your request. Please try again.';
    } finally {
      setIsGenerating(false);
    }
  }, [currentUser?.uid, aiModelData, saveModelData]); // Removed getModel dependency

  // Provide feedback to improve AI responses
  const provideFeedback = useCallback(async (messageId: string, feedback: 'positive' | 'negative', details?: string) => {
    if (!currentUser?.uid) return false; // Corrected: use currentUser
    
    try {
      await saveModelData({
        learningData: {
          feedback: [
            ...(aiModelData?.learningData?.feedback || []),
            {
              messageId,
              type: feedback,
              details: details || '',
              timestamp: new Date().toISOString()
            }
          ]
        }
      }, true);
      
      return true;
    } catch (err) {
      console.error('Error saving feedback:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    }
  }, [currentUser?.uid, aiModelData, saveModelData]); // Corrected: use currentUser

  return {
    generateResponse,
    provideFeedback,
    isGenerating,
    error
  };
};
